import type { ReplaceAssetResult } from './use-replace-asset';

/**
 * UI-agnostic descriptor for the toast that follows a "Replace file" run
 * (Issue #417, Phase 2). Keeps the message-selection logic pure and testable —
 * the caller maps it to `toast.success`/`toast.warning` + `t()`.
 */
export interface ReplaceToast {
  level: 'success' | 'warning';
  /** i18n key for the main message. */
  key: string;
  /** Interpolation params for `key`, if any. */
  params?: Record<string, unknown>;
  /** i18n key for an optional non-fatal description line. */
  descriptionKey?: string;
}

/**
 * Map a {@link ReplaceAssetResult} to the message the operator should see.
 *
 * - degraded (`usageUnavailable`): warning, names the new asset (a same-named
 *   duplicate now exists; relink manually).
 * - partial relink failure: warning, names how many relinked vs. stayed on the
 *   old file (the old asset is intentionally kept).
 * - full success: how many services were repointed (or a plain note when the
 *   asset was unused); a failed soft-delete is surfaced as a non-fatal line.
 */
export function replaceResultToast(result: ReplaceAssetResult): ReplaceToast {
  if (result.usageUnavailable) {
    // The new asset keeps the old name (Owner decision), so the name alone can't
    // distinguish it — surface the new id too for manual follow-up.
    return {
      level: 'warning',
      key: 'propagate.result.degraded',
      params: { name: result.newAssetName, id: result.newAssetId },
    };
  }

  if (result.failedServiceIds.length > 0) {
    // Name the services that stayed on the old file so the operator can retry
    // them specifically (not just a count).
    return {
      level: 'warning',
      key: 'propagate.result.partial',
      params: {
        relinked: result.relinkedServiceIds.length,
        failed: result.failedServiceIds.length,
        services: result.failedServiceIds.join(', '),
      },
    };
  }

  const count = result.relinkedServiceIds.length;
  return {
    level: 'success',
    key: count === 0 ? 'propagate.result.successNone' : 'propagate.result.success',
    ...(count > 0 ? { params: { count } } : {}),
    ...(result.retireFailed ? { descriptionKey: 'propagate.result.retireNote' } : {}),
  };
}
